// src/ui/LibrarySheet.tsx
// 읽기 상태 + 구매처를 한 시트에서 고른다.
// 검색→상세의 "서재에 추가하기"와 내 서재 롱탭의 "서재 정보 수정하기"가 같은 화면이라 하나로 합쳤다.
// 차이는 mode 뿐:
//   add  — 제목 "서재에 추가하기",   버튼 [닫기]   [추가하기]
//   edit — 제목 "서재 정보 수정하기", 버튼 [삭제하기] [수정]  + 기존 상태/구매처를 프리셋
import React, { useEffect, useRef, useState } from "react";
import { BookStore } from "@/lib/api";
import { COLOR, FW, BORDER, RADIUS } from "@/ui/tokens";

export type LibrarySheetStatus = 'READING' | 'WISHLIST' | 'FINISHED';
export type LibrarySheetMode = 'add' | 'edit';

/** 알약 모양 선택 칩. 선택되면 검정으로 채운다. */
function Chip({ label, selected, onClick }:{ label:string; selected:boolean; onClick:()=>void }) {
    return (
        <button
            onClick={onClick}
            onMouseDown={e=>e.preventDefault()}
            style={{
                padding:"11px 18px", borderRadius:999, cursor:"pointer",
                fontSize:15, fontWeight:FW.bold, lineHeight:1.2,
                whiteSpace:"nowrap",
                background: selected ? COLOR.text : COLOR.white,
                color:      selected ? COLOR.white : COLOR.text,
                border: selected ? `1px solid ${COLOR.text}` : BORDER.muted,
            }}
        >
            {label}
        </button>
    );
}

export function LibrarySheet({
                                 open, mode = 'add', stores, loading,
                                 initialStatus = null, initialStoreId = null,
                                 onClose, onSubmit, onDelete,
                             }:{
    open:boolean;
    mode?:LibrarySheetMode;
    stores:BookStore[];
    loading:boolean;
    /** edit 모드에서 시트를 열 때 미리 선택해 둘 값 */
    initialStatus?:LibrarySheetStatus|null;
    initialStoreId?:number|null;
    onClose:()=>void;
    onSubmit:(status:LibrarySheetStatus, storeId:number|null)=>void;
    /** edit 모드 전용. 없으면 삭제 버튼이 동작하지 않는다. */
    onDelete?:()=>void;
}) {
    const [status, setStatus] = useState<LibrarySheetStatus|null>(null);
    const [storeId, setStoreId] = useState<number|null>(null);
    // "+"를 누르면 더보기 구매처만 있는 화면으로 전환한다. 실서버에는 구매처가 수십 개라
    // 메인 목록에 이어붙이면 시트가 감당이 안 된다.
    const [view, setView] = useState<'main'|'more'>('main');
    const [submitting, setSubmitting] = useState(false);
    // 더보기에서 "닫기"는 취소다 — 들어오기 직전 선택으로 되돌리려고 기억해둔다.
    const storeBeforeMoreRef = useRef<number|null>(null);

    // 시트를 다시 열 때 이전 선택이 남아 있으면 안 된다.
    // edit 모드는 "이전 선택"이 아니라 그 책의 현재 값에서 시작한다.
    useEffect(() => {
        if (open) { setStatus(initialStatus); setStoreId(initialStoreId); setView('main'); setSubmitting(false); }
    }, [open, initialStatus, initialStoreId]);

    if (!open) return null;

    const isEdit = mode === 'edit';

    const primary = stores.filter(s => s.is_primary !== false);
    const more    = stores.filter(s => s.is_primary === false);
    const picked  = storeId != null ? stores.find(s => s.id === storeId) : undefined;
    // 더보기에서 고른 구매처는 메인 화면 칩 목록에 없다. 그대로 두면 골랐는데 아무 데도
    // 안 보이는 꼴이 되므로 그 항목만 뒤에 붙인다.
    const mainChips = picked && picked.is_primary === false ? [...primary, picked] : primary;

    // 읽기 상태만 필수다. 구매처는 안 골라도 된다(달력 경유 등으로 어차피 NULL이 생긴다).
    const canSubmit = !!status && !submitting;

    const submit = () => {
        if (!canSubmit) return;
        setSubmitting(true);
        onSubmit(status!, storeId);
    };

    // 더보기 화면에서 주 버튼은 제출이 아니라 고른 구매처를 확정하고 메인으로 돌아가는 것.
    // 이 화면에서 실제로 하나 고른 경우에만 열린다.
    const inMore = view === 'more';
    const moreConfirmable = storeId != null && more.some(x => x.id === storeId);
    const primaryEnabled = inMore ? moreConfirmable : canSubmit;
    const onPrimary = inMore ? () => setView('main') : submit;
    // 더보기에서의 "닫기"는 시트를 닫지 않고 고른 걸 버린 뒤 메인으로 돌아간다.
    // edit 메인 화면에서의 보조 버튼은 닫기가 아니라 삭제다.
    const onSecondary = inMore
        ? () => { setStoreId(storeBeforeMoreRef.current); setView('main'); }
        : (isEdit ? (() => { if (!submitting) onDelete?.(); }) : onClose);

    const title          = isEdit ? "서재 정보 수정하기" : "서재에 추가하기";
    const primaryLabel   = isEdit ? "수정" : "추가하기";
    const secondaryLabel = inMore ? "닫기" : (isEdit ? "삭제하기" : "닫기");

    const labelStyle: React.CSSProperties = {
        fontSize:15, fontWeight:FW.extraBold, color:COLOR.text, margin:"0 0 10px",
    };

    return (
        <div onClick={onClose}
             style={{ position:"fixed", inset:0, background:"rgba(0,0,0,.4)", display:"grid", alignItems:"end", zIndex:50 }}>
            <div onClick={e=>e.stopPropagation()}
                 style={{ background:COLOR.white, borderTopLeftRadius:20, borderTopRightRadius:20,
                          padding:"22px 20px 18px",
                          // 이 WebView에서 vh는 시스템 UI를 포함해 어긋난다. main.tsx가 재서 세팅하는 --app-lvh를 쓴다.
                          maxHeight:"calc(var(--app-lvh, 100vh) * 0.8)", display:"flex", flexDirection:"column" }}>

                <div style={{ flex:"0 0 auto", fontWeight:FW.extraBold, fontSize:19, color:COLOR.text, marginBottom:20 }}>
                    {title}
                </div>

                {/* minHeight:0 없으면 flex 아이템의 기본 min-height:auto 때문에 줄어들지 않고
                    maxHeight를 뚫고 늘어난다 → 바닥 정렬(alignItems:end)이라 내용이 화면 위로 잘려나감 */}
                <div style={{ overflowY:"auto", flex:"1 1 auto", minHeight:0, WebkitOverflowScrolling:"touch" }}>
                    {view === 'main' ? (
                        <>
                            <div style={labelStyle}>읽기 상태<span style={{ color:"#dc2626" }}>*</span></div>
                            <div style={{ display:"flex", flexWrap:"wrap", gap:10, marginBottom:24 }}>
                                <Chip label="완독한 책"   selected={status==='FINISHED'} onClick={()=>setStatus('FINISHED')} />
                                <Chip label="읽고 싶은 책" selected={status==='WISHLIST'} onClick={()=>setStatus('WISHLIST')} />
                                <Chip label="읽고 있는 책" selected={status==='READING'}  onClick={()=>setStatus('READING')} />
                            </div>

                            <div style={labelStyle}>구매처</div>
                            {loading ? (
                                <div style={{ padding:"4px 0 20px", color:"#888", fontSize:14 }}>불러오는 중…</div>
                            ) : stores.length === 0 ? (
                                <div style={{ padding:"4px 0 20px", color:"#888", fontSize:14 }}>등록된 구매처가 없습니다.</div>
                            ) : (
                                <div style={{ display:"flex", flexWrap:"wrap", gap:10, marginBottom:20 }}>
                                    {mainChips.map(s => (
                                        // 이미 고른 칩을 다시 누르면 해제된다 — 구매처는 필수가 아니다.
                                        <Chip key={s.id} label={s.name} selected={storeId===s.id}
                                              onClick={()=>setStoreId(prev => prev===s.id ? null : s.id)} />
                                    ))}
                                    {/* 더보기가 없으면 "+"를 띄우지 않는다 — 눌러도 아무것도 안 나오면 고장으로 보인다. */}
                                    {more.length > 0 && (
                                        <Chip label="+" selected={false}
                                              onClick={()=>{ storeBeforeMoreRef.current = storeId; setView('more'); }} />
                                    )}
                                </div>
                            )}

                            <div style={{ fontSize:13, color:"#888", marginBottom:18 }}>
                                <span style={{ color:"#dc2626" }}>*</span>필수 입력 항목
                            </div>
                        </>
                    ) : (
                        <>
                            <div style={labelStyle}>구매처</div>
                            <div style={{ display:"flex", flexWrap:"wrap", gap:10, marginBottom:20 }}>
                                {more.map(s => (
                                    // 여기서는 고르기만 한다. 메인으로 돌아가는 건 주 버튼.
                                    <Chip key={s.id} label={s.name} selected={storeId===s.id}
                                          onClick={()=>setStoreId(prev => prev===s.id ? null : s.id)} />
                                ))}
                            </div>

                            <div style={{ fontSize:13, color:"#888", marginBottom:18 }}>
                                <span style={{ color:"#dc2626" }}>*</span>최대 1개 선택 가능
                            </div>
                        </>
                    )}
                </div>

                <div style={{ flex:"0 0 auto", display:"flex", gap:12 }}>
                    <button onClick={onSecondary}
                            style={{ flex:1, padding:"16px 0", fontWeight:FW.extraBold, fontSize:16, border:"none",
                                     background:"#f1f1f1", color:COLOR.text, borderRadius:RADIUS.md, cursor:"pointer" }}>
                        {secondaryLabel}
                    </button>
                    <button onClick={onPrimary} disabled={!primaryEnabled}
                            style={{ flex:1, padding:"16px 0", fontWeight:FW.extraBold, fontSize:16, border:"none",
                                     background: primaryEnabled ? COLOR.text : "#c9c9c9",
                                     color:COLOR.white, borderRadius:RADIUS.md,
                                     cursor: primaryEnabled ? "pointer" : "default" }}>
                        {primaryLabel}
                    </button>
                </div>
            </div>
        </div>
    );
}
